import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import { Loader2, BarChart3, MousePointerClick, Users, Clock, ExternalLink, Sparkles } from "lucide-react";
import { API, buildHeaders } from "./shared";

function KPI({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <div className="rounded-xl border bg-white p-4" style={{ borderColor: "#e5e7eb" }} data-testid={testid}>
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center justify-center h-7 w-7 rounded-md" style={{ background: `${accent}15`, color: accent }}>
          <Icon className="h-3.5 w-3.5" />
        </span>
        <div className="text-[10.5px] font-bold tracking-[0.14em] uppercase text-[#94a3b8]">{label}</div>
      </div>
      <div className="mt-1.5 text-[24px] font-bold tracking-[-0.02em] leading-none text-[#0f172a]">{value}</div>
      {sub && <div className="mt-1.5 text-[11px] text-[#64748b]">{sub}</div>}
    </div>
  );
}

export default function TrackingTab({ session, campaign }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const headers = useMemo(() => buildHeaders(session), [session]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/campaigns/${campaign.id}/tracking`, { headers });
      setData(r.data);
    } catch { toast.error("Failed to load tracking"); }
    finally { setLoading(false); }
  }, [campaign.id, headers]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center py-14" data-testid="tracking-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#94a3b8]" />
      </div>
    );
  }

  const m = data.metrics || {};
  const maxDaily = Math.max(1, ...(data.daily || []).map((d) => d.visits));

  return (
    <div data-testid="tracking-tab" className="space-y-5">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KPI icon={BarChart3} label="Visits" value={m.visits || 0} sub="Landing page" accent="#3B4FE4" testid="tracking-kpi-visits" />
        <KPI icon={Users} label="Uniques" value={m.unique_visitors || 0} sub={`${m.visits ? Math.round((m.unique_visitors / m.visits) * 100) : 0}% unique`} accent="#16a34a" testid="tracking-kpi-uniques" />
        <KPI icon={MousePointerClick} label="Click-Through Rate" value={`${m.click_through_rate || 0}%`} sub="Visits ÷ Sent" accent="#C79A3B" testid="tracking-kpi-ctr" />
        <KPI icon={Users} label="Leads Captured" value={m.leads_captured || 0} sub="Form submissions" accent="#0f172a" testid="tracking-kpi-leads" />
        <KPI icon={Clock} label="Avg Time on Page" value={`${m.avg_time_on_page_sec || 0}s`} sub="Session length" accent="#0891b2" testid="tracking-kpi-time" />
        <KPI icon={BarChart3} label="Bounce Rate" value={`${m.bounce_rate || 0}%`} sub="Single-page sessions" accent="#dc2626" testid="tracking-kpi-bounce" />
      </div>

      {/* Daily timeseries */}
      <section className="rounded-2xl border bg-white p-5" style={{ borderColor: "#e5e7eb" }} data-testid="tracking-daily">
        <h3 className="text-[14px] font-bold text-[#0f172a] mb-3 flex items-center gap-2">
          <BarChart3 className="h-4 w-4" /> Last 7 Days
        </h3>
        <div className="flex items-end gap-2 h-32" data-testid="tracking-daily-bars">
          {(data.daily || []).map((d) => {
            const h = Math.max(4, (d.visits / maxDaily) * 100);
            return (
              <div key={d.day} className="flex-1 flex flex-col items-center gap-1.5">
                <div className="text-[10.5px] text-[#64748b]">{d.visits}</div>
                <div className="w-full rounded-t-md" style={{ height: `${h}%`, background: "#3B4FE4", minHeight: "4px" }} />
                <div className="text-[10px] font-semibold text-[#94a3b8] uppercase tracking-wider">{d.day}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Top sources */}
      <section className="rounded-2xl border bg-white p-5" style={{ borderColor: "#e5e7eb" }} data-testid="tracking-sources">
        <h3 className="text-[14px] font-bold text-[#0f172a] mb-3 flex items-center gap-2">
          <ExternalLink className="h-4 w-4" /> Top Sources
        </h3>
        <ul className="space-y-2.5">
          {(data.top_sources || []).map((s) => (
            <li key={s.source} className="flex items-center gap-3" data-testid={`tracking-source-${s.source.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[12.5px] font-semibold text-[#0f172a]">{s.source}</span>
                  <span className="text-[11px] text-[#64748b]">{s.visits} visits · {s.pct}%</span>
                </div>
                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "#f1f5f9" }}>
                  <div className="h-full rounded-full" style={{ width: `${s.pct}%`, background: "#3B4FE4" }} />
                </div>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <div className="rounded-md px-3 py-2 text-[11.5px] flex items-center gap-1.5"
        style={{ background: "rgba(199,154,59,0.10)", color: "#92400e" }}>
        <Sparkles className="h-3 w-3" />
        Tracking metrics are MOCKED in Phase 2 (deterministically derived from the campaign id). Add a real tracking pixel + landing-page event capture to make these live.
      </div>
    </div>
  );
}
